import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import {
  FaArrowLeft,
  FaUsersCog,
  FaPlus,
  FaTrash,
  FaTimes,
  FaEnvelope,
  FaLock,
  FaExclamationTriangle,
  FaRedo,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { api, getStoredUser } from "../../services/authServices";
import ConfirmModal from "../../components/ConfirmModal";

const ManageAppManagers = () => {
  const navigate = useNavigate();
  const currentUser = getStoredUser();

  const [managers, setManagers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Confirm/notice modal state (replaces window.confirm / alert)
  const [deleteTarget, setDeleteTarget] = useState(null); // manager object
  const [isDeleting, setIsDeleting] = useState(false);
  const [notice, setNotice] = useState(null); // { title, message }

  useEffect(() => {
    if (currentUser && currentUser.role !== "admin") {
      navigate("/admin-dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchManagers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/api/user/managers");
      setManagers(data.data || []);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to load app managers.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchManagers();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.email || !formData.password) {
      setFormError("Email and password are required");
      return;
    }
    if (formData.password.length < 6) {
      setFormError("Password must be at least 6 characters long");
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post("/api/user/managers", formData);
      setFormData({ email: "", password: "" });
      setShowForm(false);
      fetchManagers();
    } catch (err) {
      setFormError(
        err?.response?.data?.message || "Failed to create app manager.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget?._id;
    setIsDeleting(true);
    try {
      await api.delete(`/api/user/managers/${id}`);
      setManagers((prev) => prev.filter((m) => m._id !== id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteTarget(null);
      setNotice({
        title: "Remove failed",
        message:
          err?.response?.data?.message || "Failed to remove app manager.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "—";

  return (
    <>
      <Helmet>
        <title>App Managers | Admin Dashboard</title>
      </Helmet>

      <PageWrapper>
        <Container>
          <PageHeader>
            <BackButton onClick={() => navigate("/admin-dashboard")}>
              <FaArrowLeft /> Back to Dashboard
            </BackButton>
            <TitleRow>
              <TitleSection>
                <FaUsersCog />
                <div>
                  <h1>App Managers</h1>
                  <p>Give trusted staff access to manage shipments</p>
                </div>
              </TitleSection>
              <AddBtn onClick={() => setShowForm((s) => !s)}>
                {showForm ? <FaTimes /> : <FaPlus />}
                {showForm ? "Cancel" : "Add App Manager"}
              </AddBtn>
            </TitleRow>
          </PageHeader>

          {showForm && (
            <FormCard onSubmit={handleCreate}>
              <FormRow>
                <FormGroup>
                  <label>Email *</label>
                  <div className="input-wrapper">
                    <FaEnvelope className="input-icon" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, email: e.target.value }))
                      }
                      placeholder="manager@example.com"
                    />
                  </div>
                </FormGroup>
                <FormGroup>
                  <label>Password *</label>
                  <div className="input-wrapper">
                    <FaLock className="input-icon" />
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          password: e.target.value,
                        }))
                      }
                      placeholder="At least 6 characters"
                    />
                  </div>
                </FormGroup>
              </FormRow>

              {formError && <ErrorMessage>{formError}</ErrorMessage>}

              <FormActions>
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Creating…" : "Create App Manager"}
                </SubmitButton>
              </FormActions>
            </FormCard>
          )}

          <TableCard>
            {isLoading ? (
              <StateWrap>
                <div className="spinner" />
                <p>Loading app managers…</p>
              </StateWrap>
            ) : error ? (
              <StateWrap>
                <FaExclamationTriangle className="err-icon" />
                <p>{error}</p>
                <button onClick={fetchManagers}>
                  <FaRedo /> Retry
                </button>
              </StateWrap>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managers.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="empty-row">
                          No app managers yet. Add one to get started.
                        </td>
                      </tr>
                    ) : (
                      managers.map((m) => (
                        <tr key={m._id}>
                          <td className="email">{m.email}</td>
                          <td>
                            <RoleBadge>App Manager</RoleBadge>
                          </td>
                          <td>{formatDate(m.createdAt)}</td>
                          <td>
                            <DeleteBtn
                              title="Remove"
                              onClick={() => setDeleteTarget(m)}
                            >
                              <FaTrash />
                            </DeleteBtn>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </TableCard>
        </Container>
      </PageWrapper>

      {/* Delete confirmation (replaces window.confirm) */}
      <ConfirmModal
        open={!!deleteTarget}
        danger
        title="Remove this app manager?"
        message={
          deleteTarget
            ? `${deleteTarget.email} will lose access immediately. This action cannot be undone.`
            : ""
        }
        confirmLabel="Remove"
        loading={isDeleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      {/* Notice popup (replaces alert) */}
      <ConfirmModal
        open={!!notice}
        alertOnly
        danger
        title={notice?.title}
        message={notice?.message}
        onClose={() => setNotice(null)}
      />
    </>
  );
};

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f0f2f5;
  padding: 2rem;

  @media screen and (max-width: 600px) {
    padding: 1rem;
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #0b1e33;
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: 1rem;
  padding: 0.5rem 0;

  &:hover {
    color: #d4af37;
  }
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    font-size: 1.8rem;
    color: #d4af37;
  }

  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0b1e33;
    margin: 0 0 0.2rem;
  }

  p {
    font-size: 0.85rem;
    color: #8a9ab0;
    margin: 0;
  }
`;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.4rem;
  background: #d4af37;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.22s ease;

  &:hover {
    background: #c09b28;
    transform: translateY(-1px);
  }

  @media screen and (max-width: 420px) {
    width: 100%;
    justify-content: center;
  }
`;

const FormCard = styled.form`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .input-wrapper {
    position: relative;

    .input-icon {
      position: absolute;
      left: 0.9rem;
      top: 50%;
      transform: translateY(-50%);
      color: #a0aec0;
      font-size: 0.9rem;
    }

    input {
      width: 100%;
      padding: 0.65rem 0.75rem 0.65rem 2.4rem;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      font-size: 0.88rem;
      font-family: inherit;

      &:focus {
        outline: none;
        border-color: #d4af37;
        box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.1);
      }
    }
  }
`;

const ErrorMessage = styled.div`
  padding: 0.65rem 1rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.82rem;
  margin-top: 1rem;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1.25rem;
`;

const SubmitButton = styled.button`
  padding: 0.7rem 1.5rem;
  background: #d4af37;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #c09b28;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media screen and (max-width: 480px) {
    width: 100%;
  }
`;

const TableCard = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 480px;
  }

  thead tr {
    background: #f8f9fb;
  }

  th {
    padding: 0.9rem 1.25rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 700;
    color: #8a9ab0;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    white-space: nowrap;
  }

  td {
    padding: 0.9rem 1.25rem;
    font-size: 0.88rem;
    color: #4a5568;
    border-bottom: 1px solid #f0f2f5;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  .email {
    font-weight: 600;
    color: #0b1e33;
  }

  .empty-row {
    text-align: center;
    color: #a0aec0;
    padding: 3rem 1rem;
  }

  @media screen and (max-width: 600px) {
    th,
    td {
      padding: 0.75rem 0.9rem;
    }
  }
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #2196f3;
  background: rgba(33, 150, 243, 0.1);
`;

const DeleteBtn = styled.button`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #e53e3e;
  background: rgba(229, 62, 62, 0.1);
  transition: all 0.18s ease;

  &:hover {
    background: rgba(229, 62, 62, 0.2);
    transform: scale(1.1);
  }
`;

const StateWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 0.75rem;
  text-align: center;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: #d4af37;
    border-radius: 50%;
    animation: spin 0.75s linear infinite;
  }

  .err-icon {
    font-size: 1.8rem;
    color: #e53e3e;
  }

  p {
    font-size: 0.88rem;
    color: #718096;
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 1.1rem;
    background: #d4af37;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default ManageAppManagers;
